/**
 * SchemaDriftImpactPanel — every job a changed source breaks, on one screen.
 *
 * Shown in the Run tab after "Find every affected job"
 * (POST .../schema-drift/impact): the jobs of this project that read the
 * job's source table, then everything downstream of them (a snapshot of the
 * warehouse table, ...), in dependency order. Each job with drift has a
 * pending proposal; a person ticks the ones to approve and
 * "Approve & apply selected" (POST /schema-drift/impact/apply) applies them
 * upstream first — each in its own transaction where the target supports
 * one, each approval in the audit trail with the person's name — then reruns
 * the jobs in the same order.
 *
 * A downstream job's proposal is planned for the shape its source will have
 * once the upstream fix is applied, so unticking the upstream job unticks it
 * too (the server would skip it anyway).
 *
 * Safety: schema metadata only (names, types, DDL) — never a data value.
 */

import { useCallback, useMemo, useState } from "react";
import { Check, GitBranch, Loader2 } from "lucide-react";
import type {
  SchemaDriftImpact,
  SchemaDriftImpactApplyResult,
  SchemaDriftImpactJob,
} from "../../../features/pipeline-engineer/types";
import { applySchemaDriftImpact } from "../../../features/pipeline-engineer/api/pe-client";
import { DriftChangeRow } from "./SchemaDriftPanel";

const STATUS_CHIP: Record<
  SchemaDriftImpactJob["status"],
  { label: string; cls: string }
> = {
  pending: {
    label: "Drift: fix proposed",
    cls: "bg-amber-500/10 text-amber-300 border border-amber-500/20",
  },
  no_drift: {
    label: "No drift",
    cls: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20",
  },
  manual: {
    label: "Review by hand",
    cls: "bg-violet-500/10 text-violet-300 border border-violet-500/20",
  },
  error: {
    label: "Couldn't check",
    cls: "bg-red-500/10 text-red-400 border border-red-500/20",
  },
};

const RESULT_TEXT: Record<string, string> = {
  applied: "Applied",
  skipped: "Skipped",
  failed: "Failed",
  refused: "Refused",
};

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n !== 1 ? "s" : ""}`;
}

/** "4 of 7 jobs, in 3 of 5 pipelines," — or just "4 jobs" from an API that
 * doesn't report what it searched. */
function affectedCount(impact: SchemaDriftImpact): string {
  const { jobsSearched, pipelinesSearched, pipelinesImpacted } = impact;
  if (
    jobsSearched === undefined ||
    pipelinesSearched === undefined ||
    pipelinesImpacted === undefined
  )
    return plural(impact.jobs.length, "job");
  return (
    `${impact.jobs.length} of ${plural(jobsSearched, "job")}, ` +
    `in ${pipelinesImpacted} of ${plural(pipelinesSearched, "pipeline")},`
  );
}

/** What the rest of the search found: the jobs left out are not affected
 * (unless the walk was cut short or a job couldn't be read). */
function searchSummary(impact: SchemaDriftImpact): string {
  if (impact.jobsSearched === undefined) return "";
  const unreadable = impact.jobsUnreadable ?? 0;
  const others = impact.jobsSearched - impact.jobs.length - unreadable;
  const parts: string[] = [];
  if (others > 0 && !impact.truncated)
    parts.push(
      `The other ${plural(others, "job")} ${others === 1 ? "isn't" : "aren't"} affected.`,
    );
  if (unreadable > 0)
    parts.push(
      `${plural(unreadable, "job")} couldn't be read, so ${unreadable === 1 ? "it wasn't" : "they weren't"} checked.`,
    );
  return parts.length ? `${parts.join(" ")} ` : "";
}

function fixable(job: SchemaDriftImpactJob): boolean {
  return (
    job.status === "pending" &&
    job.proposalId !== null &&
    job.items.some((c) => c.autoFix)
  );
}

/** ``jobId`` and every job downstream of it in this check. */
function withDependents(
  jobs: SchemaDriftImpactJob[],
  jobId: string,
): Set<string> {
  const out = new Set([jobId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const j of jobs) {
      if (!out.has(j.jobId) && j.dependsOn.some((d) => out.has(d))) {
        out.add(j.jobId);
        grew = true;
      }
    }
  }
  return out;
}

export function SchemaDriftImpactPanel({
  impact,
  onApplied,
  onClose,
  onRecheck,
}: {
  impact: SchemaDriftImpact;
  /** Run the impact check again (after a partial apply, or to refresh). */
  onRecheck?: () => void;
  /** Applied (some or all); `result.runs` are the queued reruns. */
  onApplied: (result: SchemaDriftImpactApplyResult) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(impact.jobs.filter(fixable).map((j) => j.jobId)),
  );
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SchemaDriftImpactApplyResult | null>(
    null,
  );
  const nameOf = useMemo(
    () => new Map(impact.jobs.map((j) => [j.jobId, j.jobName])),
    [impact.jobs],
  );
  const pending = impact.jobs.filter(fixable);
  const chosen = pending.filter((j) => selected.has(j.jobId));

  const toggle = useCallback(
    (job: SchemaDriftImpactJob) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(job.jobId)) {
          // A downstream fix assumes this one: drop them together.
          for (const id of withDependents(impact.jobs, job.jobId))
            next.delete(id);
        } else {
          next.add(job.jobId);
          // ... and a downstream fix needs its upstream one.
          for (const d of job.dependsOn)
            if (impact.jobs.some((j) => j.jobId === d && fixable(j)))
              next.add(d);
        }
        return next;
      });
    },
    [impact.jobs],
  );

  const handleApply = useCallback(async () => {
    if (chosen.length === 0) return;
    const statements = chosen.reduce(
      (n, j) => n + j.items.filter((c) => c.autoFix && c.ddl).length,
      0,
    );
    if (
      !window.confirm(
        `Approve and apply the fixes for ${chosen.length} job${chosen.length !== 1 ? "s" : ""}? ` +
          `${statements} ALTER TABLE statement${statements !== 1 ? "s" : ""} will run, upstream jobs first, ` +
          "each job's in its own transaction where its database supports one; the mappings will be updated " +
          "and the jobs will rerun in the same order.",
      )
    )
      return;
    setError(null);
    setApplying(true);
    try {
      const res = await applySchemaDriftImpact(
        chosen.map((j) => j.proposalId as string),
      );
      setResult(res);
      onApplied(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply.");
    } finally {
      setApplying(false);
    }
  }, [chosen, onApplied]);

  const outcomeOf = (jobId: string) =>
    result?.results.find((r) => r.jobId === jobId);

  return (
    <div
      className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-3"
      aria-label="Schema drift impact"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-amber-300">
            {affectedCount(impact)} read{" "}
            <span className="font-mono">{impact.changedSourceTable}</span> or a
            table loaded from it; {pending.length} need
            {pending.length === 1 ? "s" : ""} a fix
          </p>
          <p className="text-xs text-white/40 mt-0.5">
            {searchSummary(impact)}Upstream jobs first. Tick the fixes to
            approve.
          </p>
        </div>
        <div className="flex gap-2">
          {onRecheck && (
            <button
              type="button"
              onClick={onRecheck}
              disabled={applying}
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-amber-300 border border-amber-500/20 rounded-lg text-xs font-medium disabled:opacity-50"
            >
              Re-check
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 border border-white/10 rounded-lg text-xs font-medium disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={applying || chosen.length === 0 || result !== null}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <Check size={12} aria-hidden="true" />
            )}
            Approve &amp; apply selected ({chosen.length})
          </button>
        </div>
      </div>

      {impact.truncated && (
        <p className="text-amber-300 text-xs" role="alert">
          Stopped at {impact.maxJobs} jobs: more jobs may read this source. Fix
          these, then re-check.
        </p>
      )}
      {error !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {error}
        </p>
      )}
      {result !== null && (
        <p className="text-emerald-400 text-xs" role="status">
          {result.results.filter((r) => r.status === "applied").length} of{" "}
          {result.results.length} fixes applied; {result.runs.length} job
          {result.runs.length !== 1 ? "s" : ""} rerunning upstream first.
        </p>
      )}

      <ul className="space-y-3">
        {impact.jobs.map((job) => {
          const outcome = outcomeOf(job.jobId);
          const chip = STATUS_CHIP[job.status];
          return (
            <li
              key={job.jobId}
              className="bg-black/20 border border-white/5 rounded-lg p-3 space-y-2"
              style={{ marginLeft: `${Math.min(job.level, 4) * 16}px` }}
              aria-label={`${job.pipelineName} / ${job.jobName}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {fixable(job) && (
                  <input
                    type="checkbox"
                    checked={selected.has(job.jobId)}
                    onChange={() => toggle(job)}
                    disabled={applying || result !== null}
                    aria-label={`Approve the fix for ${job.jobName}`}
                  />
                )}
                {job.level > 0 && (
                  <GitBranch
                    size={12}
                    className="text-white/40"
                    aria-hidden="true"
                  />
                )}
                <span className="text-xs text-white/50">
                  {job.pipelineName} /
                </span>
                <span className="text-xs font-medium text-white/85">
                  {job.jobName}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${chip.cls}`}
                >
                  {chip.label}
                </span>
                {outcome && (
                  <span
                    className={`text-xs font-medium ${outcome.status === "applied" ? "text-emerald-400" : "text-red-400"}`}
                  >
                    {RESULT_TEXT[outcome.status] ?? outcome.status}
                    {outcome.detail ? `: ${outcome.detail}` : ""}
                    {outcome.rerun && outcome.rerun !== "queued"
                      ? ` (rerun ${outcome.rerun})`
                      : ""}
                  </span>
                )}
              </div>
              <p className="text-xs font-mono text-white/40">
                {job.sourceTable ?? "custom SQL"} → {job.targetTable}
                {job.dependsOn.length > 0 &&
                  ` · reads the target of ${job.dependsOn
                    .map((d) => nameOf.get(d) ?? d)
                    .join(", ")}`}
              </p>
              {job.predicted && (
                <p className="text-xs text-white/50">
                  Planned for the shape its source will have once the upstream
                  fix is applied.
                </p>
              )}
              {job.detail && (
                <p className="text-xs text-white/60">{job.detail}</p>
              )}
              {job.items.length > 0 && (
                <ul className="space-y-2">
                  {job.items.map((change) => (
                    <DriftChangeRow key={change.changeId} change={change} />
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-xs text-white/30">
        Each approval is recorded in the audit trail with your name. A fix whose
        upstream fix isn't applied is skipped, never applied against a source
        that doesn't have the new shape. Unticked proposals stay pending.
      </p>
    </div>
  );
}
